import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import {
  getActiveStateOptions,
  getSourceOptions,
  getStatusOptions,
} from '../../containers/strategy-v2/initiative-list/filters/filters.helpers';
import { CostMode } from '../../containers/strategy-v2/initiative-performance/cost-tab/cost-tab.type';
import { getInitialVisiblePhases } from '../../containers/strategy-v2/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.helpers';
import {
  EpicSortOption,
  FocusMode,
  PerformanceTab,
  SortDirection,
  ThroughputMode,
} from '../../containers/strategy-v2/initiative-performance/initiative-performance.type';
import { RelatedTasksMode } from '../../containers/strategy-v2/initiative-performance/related-tasks-tab/related-tasks-tab.type';
import { MeasureDataType } from '../../containers/strategy-v2/initiative-performance/targets-tab/targets-tab.type';
import { Metric } from '../../containers/strategy-v2/strategy-v2.type';
import { isLoggingEnabled, logger } from '../logger/logger';
import {
  CostTabState,
  FlowByPhaseTabState,
  FocusTabState,
  Panel,
  State,
  TargetsTabState,
  ThroughputTabState,
  ThroughputType,
  TreeMapMetric,
  View,
  type RelatedTasksTabState,
} from './strategy-store.type';

const defaultFilters = {
  status: getStatusOptions().map((option) => option.value),
  state: [],
  source: getSourceOptions().map((option) => option.value),
  is_active: getActiveStateOptions().map((option) => option.value),
};

const useStrategyStore = create<State>()(
  devtools(
    persist(
      logger((_) => ({
        initiatives: [],
        kpiOverviewData: null,
        view: View.Table,
        page: 1,
        selectedInitiative: null,
        filters: defaultFilters,
        collapsedGroups: {
          status: false,
          state: false,
          source: false,
          is_active: false,
        },
        activePanel: Panel.Filters,
        searchTerm: '',
        treeMapMetric: TreeMapMetric.CostByInitiative,
        epics: [],
        initiativePerformance: null,
        performanceTab: PerformanceTab.Throughput,
        performanceActivePanel: Panel.Filters,
        throughputMode: ThroughputMode.InitiativeProgress,
        message: null,
      })),
      {
        name: 'strategy-storage',
        storage: createJSONStorage(() => localStorage),
        version: 2,
        migrate: (persistedState, version) => {
          if (version === 1) {
            // if the stored value is in version 0, we rename the field to the new name
            (persistedState as any).filters.is_active = getActiveStateOptions().map((option) => option.value);
          }

          return persistedState;
        },
        partialize: (state) =>
          Object.fromEntries(
            Object.entries(state).filter(([key]) => !['page', 'searchTerm', 'performanceTab'].includes(key)),
          ),
      },
    ),
    {
      name: 'Bloomfilter - Strategy Store',
      enabled: isLoggingEnabled,
    },
  ),
);

const throughputTabInitialState: ThroughputTabState = {
  mode: ThroughputMode.InitiativeProgress,
  metric: Metric.Tasks,
  selectedEpics: [],
  selectedTeams: [],
  selectedEpic: null,
  selectedTeam: null,
  selectedDate: null,
  visibleThroughputTypes: {
    [ThroughputType.Total]: true,
    [ThroughputType.Completed]: true,
  },
};

const useThroughputTabStore = create<ThroughputTabState>()(
  devtools(
    logger((_) => ({
      ...throughputTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Throughput Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const focusTabInitialState: FocusTabState = {
  mode: FocusMode.ContributingTeams,
  metric: Metric.Tasks,
  selectedTeams: [],
  selectedBoards: [],
  focusTeamsData: null,
  focusBoardsData: null,
};

const useFocusTabStore = create<FocusTabState>()(
  devtools(
    logger((_) => ({
      ...focusTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Focus Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const flowByPhaseTabInitialState: FlowByPhaseTabState = {
  metric: Metric.Tasks,
  selectedDate: null,
  selectedEpic: null,
  visiblePhases: getInitialVisiblePhases(),
  data: null,
  filterControls: {
    searchTerm: '',
    sortBy: EpicSortOption.ID,
    sortDirection: SortDirection.Asc,
    hideEmpty: false,
  },
};

const useFlowByPhaseTabStore = create<FlowByPhaseTabState>()(
  devtools(
    logger((_) => ({
      ...flowByPhaseTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Flow By Phase Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const targetsTabInitialState: TargetsTabState = {
  targets: [],
  selectedTarget: null,
  availableMeasures: [],
  measureData: null,
  visibleMeasures: {
    [MeasureDataType.Actual]: true,
    [MeasureDataType.Target]: true,
  },
};

const useTargetsTabStore = create<TargetsTabState>()(
  devtools(
    logger((_) => ({
      ...targetsTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Targets Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const costTabInitialState: CostTabState = {
  mode: CostMode.ContributingEpics,
  selectedEpics: [],
  selectedTeams: [],
  filterControls: {
    searchTerm: '',
    sortBy: EpicSortOption.ID,
    sortDirection: SortDirection.Asc,
    hideEmpty: false,
  },
  selectedEpic: null,
  selectedTeam: null,
};

const useCostTabStore = create<CostTabState>()(
  devtools(
    logger((_) => ({
      ...costTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Cost Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const relatedTasksTabInitialState: RelatedTasksTabState = {
  mode: RelatedTasksMode.ContributingTasks,
  selectedEpic: null,
  selectedTeam: null,
  selectedDate: null,
  selectedTypes: [],
  selectedPhase: null,
  searchTerm: '',
  page: 1,
  selectedTask: null,
  filterControls: {
    searchTerm: '',
    sortBy: EpicSortOption.ID,
    sortDirection: SortDirection.Asc,
    hideEmpty: false,
  },
  selectedThroughput: 'total',
  tasks: [],
};

const useRelatedTasksTabStore = create<RelatedTasksTabState>()(
  devtools(
    logger((_) => ({
      ...relatedTasksTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Related Tasks Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

export {
  costTabInitialState,
  defaultFilters,
  flowByPhaseTabInitialState,
  focusTabInitialState,
  relatedTasksTabInitialState,
  targetsTabInitialState,
  throughputTabInitialState,
  useCostTabStore,
  useFlowByPhaseTabStore,
  useFocusTabStore,
  useRelatedTasksTabStore,
  useStrategyStore,
  useTargetsTabStore,
  useThroughputTabStore,
};
